/* eslint-disable camelcase */
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { arrayOf, shape, string, bool, number, func } from "prop-types";
import dayjs from "@shared/dayjs";
import { tdpTasks, tdpDetails } from "../../../../shared/HOCs/withRedux";

import { taskSelect } from "../actions/creators";
import { editTaskAsThunk } from "../actions/thunks";
import { defaultState } from "../ModalAddEditTask/constants";

import withFilters from "../HOCs/withFilters";
import TASK_STYLES from "./constants";
import {
  IS_TASK_SELECTION_MODE,
  UUID,
  SELECTED_TASK,
  CURRENT_USER,
} from "../../../../shared/reduxRoutes/tdpTasksRoutes";

import Checkbox from "../../../../shared/v2/Checkbox";
import IconButton from "../../../../shared/v2/IconButton";
import { Trash } from "../../../../shared/v2/Icomoon";
import TaskAvatar from "../TaskAvatar";
import ModalShowTask from "../ModalShowTask";
import ModalDeleteTask from "../ModalDeleteTask";
import TaskItemNameFrame from "../TaskItemNameFrame";
import TaskInstructions from "../TaskInstructions";
import VisibilityView from "./fractals";
import Tooltip from "../../../../shared/v2/Tooltip";
import { contingentText } from "../ModalShowTask/helpers";
import TaskModal from "../../../../Tasks/components/TaskModal";
import { dispatchEditedTasks } from "../actions/helpers";
import { createTask } from "../api/helpers";

const Task = ({
  currentUser,
  task,
  onEditTaskAsThunk,
  onTaskSelect,
  uuid,
  taskFilters,
  isTasksSelectionMode,
  selectedTasks,
  useCompletedUser,
}) => {
  const dispatch = useDispatch();
  const {
    name,
    assigned_person,
    priority,
    due_on,
    externally_visible,
    id,
    completed_at,
    assigned_plan: assignedPlan,
    completed_by,
    contingent,
    instructions,
  } = task;
  const [isTaskModalOpen, setTaskModalOpen] = useState(false);
  const [isTaskModalDeleteOpen, setTaskModalDelete] = useState(false);
  const [isDeleteFromRow, setDeleteFromRow] = useState(false);
  const [isModalAddTaskOpen, setModalAddTaskOpen] = useState(false);

  const [checked, toggleChecked] = useState(false);
  useEffect(() => {
    toggleChecked(!!completed_at);
  }, [completed_at]);

  const displayUser = (useCompletedUser ? completed_by : assigned_person) || assigned_person;
  const displayDate = useCompletedUser
    ? completed_at
    : dayjs(due_on).isValid() && dayjs(due_on).format("MM/DD/YY");

  return (
    <>
      <ModalShowTask
        isTaskModalOpen={isTaskModalOpen}
        setTaskModalOpen={setTaskModalOpen}
        task={task}
        setTaskModalDelete={(value) => {
          setDeleteFromRow(false);
          setTaskModalDelete(value);
        }}
        setModalAddTaskOpen={setModalAddTaskOpen}
      />
      <ModalDeleteTask
        setTaskModalOpen={isDeleteFromRow ? () => setDeleteFromRow(false) : setTaskModalOpen}
        isTaskModalDeleteOpen={isTaskModalDeleteOpen}
        setTaskModalDelete={setTaskModalDelete}
        ids={[id]}
        type={task.type === "Appointment" ? "Appointment" : "Task"}
      />
      {isModalAddTaskOpen && (
        <TaskModal
          v4TaskApi
          listingUuid={uuid}
          task={task}
          onClose={(updatedTask) => {
            if (updatedTask) dispatchEditedTasks(dispatch, {})([createTask(updatedTask)], taskFilters);
            setModalAddTaskOpen(false);
          }}
        />
      )}
      <div
        className={`tw-group tw-h-[64px] tw-w-full tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-b-[1px] tw-border-solid tw-border-neutral-gray-50 tw-flex tw-items-center ${
          selectedTasks.includes(id) && TASK_STYLES.taskSelected
        }`}
      >
        {isTasksSelectionMode ? (
          <Checkbox
            value={null}
            className="tw-cursor-pointer tw-border-neutral-gray-50"
            checked={selectedTasks.includes(id)}
            onClick={() => {
              onTaskSelect(id);
            }}
          />
        ) : (
          <Checkbox
            className="tw-cursor-pointer"
            value={null}
            checked={checked}
            onClick={() => {
              if (task.type === "Appointment" && !checked) {
                setModalAddTaskOpen(true);
                return;
              }

              toggleChecked((prev) => !prev);
              onEditTaskAsThunk(
                {
                  ...defaultState(uuid, task),
                  ...(() =>
                    checked
                      ? { run: true }
                      : { completed: "true", completed_by_user_id: currentUser.uuid })(),
                  id,
                },
                uuid,
                taskFilters,
              );
            }}
          />
        )}
        <TaskItemNameFrame
          hasAssignedPlan={!!assignedPlan}
          assignedPlanName={assignedPlan?.name}
          nameOnClick={() => setTaskModalOpen((prev) => !prev)}
          name={name}
        />
        <div className="tw-ml-16px tw-flex tw-shrink-0 tw-items-center tw-gap-24px">
          <div className="tw-w-[44px] tw-flex tw-items-center tw-gap-[4px]">
            <div className="tw-w-[20px] tw-flex tw-justify-center">
              <VisibilityView visibility={externally_visible} />
            </div>
            {instructions && <TaskInstructions instructions={instructions} />}
          </div>
          <Tooltip
            placement="top"
            trigger={
              <div className="tw-w-[56px] tw-text-12px tw-leading-[16px] tw-font-semibold tw-text-neutral-gray-75">
                {displayDate || "TBD"}
              </div>
            }
            content={contingentText(contingent, due_on)}
          />
          <TaskAvatar assignedPerson={displayUser} />
          <div className="tw-ml-12px tw-rounded-full tw-h-24px tw-w-24px tw-border-neutral-gray-50 tw-border-1px tw-border-solid tw-flex tw-items-center tw-justify-center">
            {priority}
          </div>
          <div className="tw-w-24px tw-flex tw-justify-center">
            {!isTasksSelectionMode && (
              <IconButton
                schema="tertiary"
                size="small"
                aria-label="Delete task"
                className="tw-opacity-0 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100"
                onClick={() => {
                  setDeleteFromRow(true);
                  setTaskModalDelete(true);
                }}
              >
                <Trash size="m" />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

Task.propTypes = {
  task: shape({
    assigned_person: shape({}),
    completed_at: string,
    due_on: string,
    dueness: string,
    externally_visible: bool,
    id: string,
    name: string,
    priority: number,
    state: string,
  }),
  onEditTaskAsThunk: func,
  onTaskSelect: func,
  isTasksSelectionMode: bool,
  uuid: string,
  taskFilters: string,
  selectedTasks: arrayOf(string),
  useCompletedUser: bool,
  currentUser: shape({
    uuid: string.isRequired,
  }).isRequired,
};

Task.defaultProps = {
  task: {
    assigned_person: null,
    completed_at: null,
    due_on: null,
    dueness: null,
    externally_visible: false,
    id: null,
    name: null,
    priority: 0,
    state: null,
  },
  onEditTaskAsThunk: null,
  onTaskSelect: null,
  isTasksSelectionMode: false,
  uuid: "",
  taskFilters: "",
  selectedTasks: [],
  useCompletedUser: false,
};

const mapDispatchToProps = (dispatch) => ({
  onEditTaskAsThunk: (payload, uuid, filtered) => dispatch(editTaskAsThunk(payload, uuid, filtered)),
  onTaskSelect: (id) => dispatch(taskSelect(id)),
});

const TaskWrapped = connect(null, mapDispatchToProps)(withFilters(Task));

const TaskWrapComp = tdpTasks({
  WrappedComponent: TaskWrapped,
  states: [IS_TASK_SELECTION_MODE, UUID, SELECTED_TASK],
});

export default tdpDetails({
  WrappedComponent: TaskWrapComp,
  states: [CURRENT_USER],
});
